import {
    AlertCircle,
    ArrowLeft,
    Building,
    Calendar,
    CalendarDays,
    Clock,
    CreditCard,
    DollarSign,
    Download,
    FileText,
    Home,
    Image as ImageIcon,
    Loader,
    Mail,
    MapPin,
    Phone,
    Printer,
    Share2,
    User
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getInvoiceById } from '../../../../api/action/invoice';
import { getOrdersByOrderId } from '../../../../api/action/order';
import { DOMAIN_TYPES, getDomainColorsByType, getDomainLabelsByType } from '../../../../domain/Domain';
import {
    formatCurrency as formatSharedCurrency,
    formatDateInCompanyTimeZone,
    formatPhoneNumber as formatSharedPhoneNumber
} from '../../../../utils/format';
import InvoiceDownload from '../../../invoice/InvoiceDownload';

const STATUS_OPTIONS = [
    { value: 'PAID', label: 'Paid', color: 'bg-green-100 text-green-800 border-green-200' },
    { value: 'PENDING', label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    { value: 'OVERDUE', label: 'Overdue', color: 'bg-red-100 text-red-800 border-red-200' },
    { value: 'CANCELLED', label: 'Cancelled', color: 'bg-gray-100 text-gray-800 border-gray-200' }
];

const ORDER_STATUS_OPTIONS = [
    { value: 'CREATED', label: 'Created', color: 'bg-blue-100 text-blue-800 border-blue-200' },
    { value: 'PROCESSING', label: 'Processing', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    { value: 'SHIPPED', label: 'Shipped', color: 'bg-purple-100 text-purple-800 border-purple-200' },
    { value: 'DELIVERED', label: 'Delivered', color: 'bg-green-100 text-green-800 border-green-200' },
    { value: 'CANCELLED', label: 'Cancelled', color: 'bg-red-100 text-red-800 border-red-200' }
];

const printReceiptNode = (sourceNode, title) => {
    if (!sourceNode) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const width = sourceNode.scrollWidth || 794;
    const inheritedStyles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
        .map((node) => node.outerHTML)
        .join('\n');

    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
            <head>
                <title>${String(title || 'Receipt').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</title>
                ${inheritedStyles}
                <style>
                    * { box-sizing: border-box; }
                    html, body { margin: 0; padding: 0; background: #fff; }
                    body {
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .print-shell {
                        width: ${width}px;
                        margin: 0 auto;
                        background: #fff;
                    }
                    @page { margin: 0; size: A4 portrait; }
                </style>
            </head>
            <body>
                <div class="print-shell">${sourceNode.outerHTML}</div>
                <script>
                    window.onload = function () {
                        setTimeout(function () { window.print(); }, 150);
                    };
                    window.onafterprint = function () { window.close(); };
                </script>
            </body>
        </html>
    `);
    printWindow.document.close();
};

const getPrimaryReservationDate = (lineItems = []) => {
    const reservationDates = lineItems
        .map((item) =>
            item?.scheduledProduct?.period?.effectiveDate?.localStartDate
            || item?.scheduledProduct?.period?.effectiveStartDate
            || ''
        )
        .filter(Boolean)
        .sort((leftDate, rightDate) => new Date(leftDate) - new Date(rightDate));

    return reservationDates[0] || '';
};

function ViewInvoice() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);
    const authUser = useSelector((state) => state?.auth?.data) || {};

    const [invoice, setInvoice] = useState(null);
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [actionNotice, setActionNotice] = useState(null);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const receiptContainerRef = useRef(null);

    const domainType = localStorage.getItem("domainType") || DOMAIN_TYPES.RETAIL;
    const colors = getDomainColorsByType(domainType);
    const labels = getDomainLabelsByType(domainType);
    const primaryColor = colors.primary || 'var(--flex-primary, #2563eb)';
    const secondaryColor = colors.secondary || 'var(--flex-secondary, #0f172a)';
    const primarySoftColor = `color-mix(in srgb, ${primaryColor} 12%, transparent)`;
    const primaryBorderColor = `color-mix(in srgb, ${primaryColor} 24%, transparent)`;
    const invoiceBrandStyle = {
        '--invoice-primary': primaryColor,
        '--invoice-secondary': secondaryColor,
        '--invoice-primary-soft': primarySoftColor,
        '--invoice-primary-border': primaryBorderColor
    };
    const formatContext = invoice?.company || order?.company || authUser?.account?.company || domainType;

    useEffect(() => {
        if (id) {
            localStorage.setItem("invoiceprimaryId", id);
        }
    }, [id]);

    const handleDownload = () => {
        setShowDownloadModal(true);
    };

    useEffect(() => {
        const fetchInvoiceAndOrderDetails = async () => {
            if (!id) return;

            setLoading(true);
            setError(null);

            try {
                const invoiceData = await dispatch(getInvoiceById(id));
                setInvoice(invoiceData);

                if (invoiceData?.order?.primaryId) {
                    const orderData = await dispatch(getOrdersByOrderId(invoiceData.order.primaryId));
                    setOrder(Array.isArray(orderData) ? orderData[0] : orderData);
                }
            } catch (error) {
                setError(error.message || 'Failed to load invoice details');
            } finally {
                setLoading(false);
            }
        };

        fetchInvoiceAndOrderDetails();
    }, [id, dispatch]);

    const formatDate = (dateString) => (
        formatDateInCompanyTimeZone(dateString, formatContext, 'MM/dd/yyyy', '')
    );

    const formatDateTime = (dateString) => (
        formatDateInCompanyTimeZone(dateString, formatContext, 'MMMM d, yyyy, h:mm a', '')
    );

    // localStartDate/localEndDate are already wall-clock values in the company's
    // timezone (no UTC offset), so they must be displayed as-is rather than run
    // through formatDateInCompanyTimeZone, which would re-apply the company's
    // offset on top of the browser's local parsing and shift the displayed time.
    const formatWallClockDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return Number.isNaN(date.getTime())
            ? ''
            : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatWallClockTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return Number.isNaN(date.getTime())
            ? ''
            : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    const formatCurrency = (amount) => (
        formatSharedCurrency(amount ?? 0, formatContext)
    );

    const formatPhoneNumber = (phoneNumber, countryCode) => {
        if (!phoneNumber) return '';
        return formatSharedPhoneNumber(phoneNumber, countryCode || formatContext);
    };

    const formatBookingDuration = (scheduledProduct) => {
        if (!scheduledProduct?.period) return null;

        const period = scheduledProduct.period;
        if (!period.effectiveDate?.localStartDate || !period.effectiveDate?.localEndDate) {
            return null;
        }

        try {
            const dateStr = formatWallClockDate(period.effectiveDate.localStartDate);
            const startTime = formatWallClockTime(period.effectiveDate.localStartDate);
            const endTime = formatWallClockTime(period.effectiveDate.localEndDate);

            let durationStr = '';
            if (period.duration && period.durationType) {
                const formattedType = period.durationType.charAt(0).toUpperCase() + period.durationType.slice(1).toLowerCase();
                durationStr = `${period.duration} ${formattedType}s`;
            }

            return {
                dateStr,
                startTime,
                endTime,
                durationStr,
                fullString: `${dateStr}, ${startTime} - ${endTime} (${durationStr})`,
                timeZone: period.effectiveDate.timeZone || 'Local Time'
            };
        } catch {
            return null;
        }
    };

    const getScheduledProduct = (lineItem) => {
        return lineItem?.scheduledProduct;
    };

    const getDocumentType = () => {
        const receiptLabel = labels.receipt || 'Receipt';

        if (
            domainType === DOMAIN_TYPES.FLEX_OFFICE ||
            domainType === DOMAIN_TYPES.EDUCATION
        ) {
            return receiptLabel;
        }

        return invoice?.type === 'RECEIPT' ? receiptLabel : 'Invoice';
    };

    const documentType = getDocumentType();

    const handlePayNow = async () => {
        setPaymentProcessing(true);
        setActionNotice(null);
        try {
            const phoneNumber = invoice?.user?.phoneNumber || '';

            if (!phoneNumber) {
                throw new Error('Customer phone number not found');
            }
            const paymentState = {
                phoneNumber,
                invoiceId: id,
                amount: order?.totalAmount || invoice.order?.totalAmount || 0,
                order: order || invoice?.order,
                user: invoice?.user || '',
                invoice: invoice || '',
            };
            const shouldSkipOtp = Boolean(isAuthenticated || authUser?.primaryId || authUser?.email);

            navigate(shouldSkipOtp ? '/invoice-payment' : '/confirm-login', {
                state: paymentState
            });

        } catch (error) {
            setActionNotice({
                type: 'error',
                message: error.message || 'Payment failed to initiate. Please try again.'
            });
        } finally {
            setPaymentProcessing(false);
        }
    };

    const handlePrint = () => {
        if (!receiptContainerRef.current) return;
        printReceiptNode(receiptContainerRef.current, `${documentType} ${invoice?.number || invoice?.id || ''}`);
    };

    const handleShare = async () => {
        const shareTitle = `${documentType} ${invoice.number || invoice.id}`;
        const shareText = `${shareTitle} - ${formatCurrency(order?.totalAmount || invoice.total || invoice.orderTotal)}`;

        setActionNotice(null);

        try {
            if (navigator.share) {
                await navigator.share({
                    title: shareTitle,
                    text: shareText,
                    url: window.location.href
                });
                return;
            }

            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(window.location.href);
                setActionNotice({
                    type: 'success',
                    message: `${documentType} link copied to clipboard.`
                });
                return;
            }

            setActionNotice({
                type: 'warning',
                message: 'Sharing is unavailable in this browser.'
            });
        } catch (shareError) {
            if (shareError?.name !== 'AbortError') {
                setActionNotice({
                    type: 'error',
                    message: 'Unable to share this document right now.'
                });
            }
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    const handleGoHome = () => {
        navigate('/');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center" style={invoiceBrandStyle}>
                <div className="text-center">
                    <Loader className="animate-spin h-12 w-12 invoice-brand-text mx-auto mb-4" />
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center" style={invoiceBrandStyle}>
                <div className="text-center">
                    <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading {documentType}</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={handleBack}
                        className="px-4 py-2 invoice-brand-gradient text-white rounded-lg transition-all shadow-sm"
                    >
                        Back to Documents
                    </button>
                </div>
            </div>
        );
    }

    if (!invoice) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center" style={invoiceBrandStyle}>
                <div className="text-center">
                    <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">{documentType} not found</p>
                    <button
                        onClick={handleBack}
                        className="px-4 py-2 invoice-brand-gradient text-white rounded-lg transition-all shadow-sm"
                    >
                        Back to Documents
                    </button>
                </div>
            </div>
        );
    }

    const statusConfig = STATUS_OPTIONS.find(s => s.value === invoice.status) || STATUS_OPTIONS[1];
    const orderStatusConfig = order ? ORDER_STATUS_OPTIONS.find(s => s.value === order.currentStatus) : null;
    const totalAmount = order?.totalAmount || invoice.order?.totalAmount || 0;
    const subtotal = order?.totalBeforTax || order?.subtotalAmount || totalAmount;
    const lineItems = order?.lineItemList || invoice.items || [];
    const isPaid = invoice.status === 'PAID';
    const reservationDateValue = getPrimaryReservationDate(lineItems);

    const companyPhone = formatPhoneNumber(
        invoice.company?.contactPerson?.phoneNumber,
        invoice.company?.contactPerson?.countryCode
    );

    const customerPhone = formatPhoneNumber(
        invoice.user?.phoneNumber,
        invoice.user?.countryCode
    );

    return (
        <div className="min-h-screen bg-white" style={{ paddingTop: '120px', ...invoiceBrandStyle }}>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleBack}
                                className="p-2 text-gray-500 invoice-brand-action rounded-lg transition-all"
                                title="Back to invoices"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <button
                                onClick={handleGoHome}
                                className="p-2 text-gray-500 invoice-brand-action rounded-lg transition-all"
                                title="Go to home"
                            >
                                <Home size={20} />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    {documentType} #{invoice.number || invoice.id}
                                </h1>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}>
                                        {statusConfig.label}
                                    </span>
                                    {orderStatusConfig && (
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${orderStatusConfig.color}`}>
                                            {orderStatusConfig.label}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {!isPaid && (
                                <button
                                    onClick={handlePayNow}
                                    disabled={paymentProcessing}
                                    className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg flex items-center gap-2 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                >
                                    <CreditCard size={16} />
                                    {paymentProcessing ? 'Processing...' : 'Pay'}
                                </button>
                            )}

                            <button
                                onClick={handlePrint}
                                className="px-4 py-2.5 border border-gray-300 invoice-brand-action text-gray-700 rounded-lg flex items-center gap-2 transition-all text-sm"
                            >
                                <Printer size={16} />
                                Print
                            </button>
                            <button
                                onClick={handleDownload}
                                className="px-4 py-2.5 border invoice-brand-button rounded-lg flex items-center gap-2 transition-all text-sm"
                            >
                                <Download size={16} />
                                Download
                            </button>

                            <button
                                onClick={handleShare}
                                className="px-4 py-2.5 border invoice-brand-link-button rounded-lg flex items-center gap-2 transition-all text-sm"
                            >
                                <Share2 size={16} />
                                Share
                            </button>
                        </div>
                    </div>
                    {actionNotice && (
                        <div
                            className={`mb-6 rounded-lg border px-4 py-3 text-sm font-medium ${
                                actionNotice.type === 'success'
                                    ? 'border-green-200 bg-green-50 text-green-800'
                                    : actionNotice.type === 'warning'
                                        ? 'border-yellow-200 bg-yellow-50 text-yellow-800'
                                        : 'border-red-200 bg-red-50 text-red-800'
                            }`}
                            role="status"
                        >
                            {actionNotice.message}
                        </div>
                    )}
                </div>

                <div ref={receiptContainerRef} className="receipt-container bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-8" style={invoiceBrandStyle}>
                    <div className="invoice-brand-gradient px-8 py-6">
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                            <div className="text-center sm:text-left">
                                <h2 className="text-2xl font-bold text-white">{documentType}</h2>
                                <p className="text-blue-100 mt-1 text-sm">#{invoice.number || invoice.id}</p>
                            </div>
                            {invoice.company?.logoImageContent?.displayLink && (
                                <div className="bg-white p-2 rounded-lg">
                                    <img
                                        src={invoice.company?.logoImageContent?.displayLink}
                                        alt={invoice.company.name}
                                        className="h-10 w-auto object-contain"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="p-6 sm:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <Building size={18} className="invoice-brand-text" />
                                    <h3 className="text-lg font-semibold text-gray-900">From</h3>
                                </div>
                                <div className="space-y-2">
                                    <p className="font-semibold text-gray-900 text-lg">{invoice.company?.name}</p>

                                    {invoice.company?.contactPerson?.email && (
                                        <div className="flex items-start gap-2 text-sm text-gray-600">
                                            <Mail size={14} className="mt-0.5 flex-shrink-0" />
                                            <a href={`mailto:${invoice.company.contactPerson.email}`} className="invoice-brand-text hover:underline">
                                                {invoice.company.contactPerson.email}
                                            </a>
                                        </div>
                                    )}

                                    {companyPhone && (
                                        <div className="flex items-start gap-2 text-sm text-gray-600">
                                            <Phone size={14} className="mt-0.5 flex-shrink-0" />
                                            <a href={`tel:${companyPhone.replace(/\s+/g, '')}`} className="invoice-brand-text hover:underline">
                                                {companyPhone}
                                            </a>
                                        </div>
                                    )}

                                    {invoice.company?.address && (
                                        <div className="flex items-start gap-2 text-sm text-gray-600">
                                            <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                                            <div>
                                                {invoice.company.address.streetName1 && (
                                                    <p>{invoice.company.address.streetName1}</p>
                                                )}
                                                {invoice.company.address.streetName2 && (
                                                    <p>{invoice.company.address.streetName2}</p>
                                                )}
                                                <p>
                                                    {invoice.company.address.city}
                                                    {invoice.company.address.state && `, ${invoice.company.address.state}`}
                                                    {invoice.company.address.zipcode && ` ${invoice.company.address.zipcode}`}
                                                </p>
                                                {invoice.company.address.country && (
                                                    <p>{invoice.company.address.country}</p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <User size={18} className="invoice-brand-text" />
                                    <h3 className="text-lg font-semibold text-gray-900">To</h3>
                                </div>
                                <div className="space-y-2">
                                    <p className="font-semibold text-gray-900 text-lg">
                                        {invoice.user?.firstName || invoice.user?.lastName
                                            ? `${invoice.user.firstName || ''} ${invoice.user.lastName || ''}`.trim()
                                            : invoice.user?.email}
                                    </p>

                                    {invoice.user?.email && (
                                        <div className="flex items-start gap-2 text-sm text-gray-600">
                                            <Mail size={14} className="mt-0.5 flex-shrink-0" />
                                            <a href={`mailto:${invoice.user.email}`} className="invoice-brand-text hover:underline">
                                                {invoice.user.email}
                                            </a>
                                        </div>
                                    )}

                                    {customerPhone && (
                                        <div className="flex items-start gap-2 text-sm text-gray-600">
                                            <Phone size={14} className="mt-0.5 flex-shrink-0" />
                                            <a href={`tel:${customerPhone.replace(/\s+/g, '')}`} className="invoice-brand-text hover:underline">
                                                {customerPhone}
                                            </a>
                                        </div>
                                    )}

                                    {invoice.user?.addressList?.[0] && (
                                        <div className="flex items-start gap-2 text-sm text-gray-600">
                                            <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                                            <div>
                                                {invoice.user.addressList[0].streetName1 && (
                                                    <p>{invoice.user.addressList[0].streetName1}</p>
                                                )}
                                                {invoice.user.addressList[0].streetName2 && (
                                                    <p>{invoice.user.addressList[0].streetName2}</p>
                                                )}
                                                <p>
                                                    {invoice.user.addressList[0].city}
                                                    {invoice.user.addressList[0].state && `, ${invoice.user.addressList[0].state}`}
                                                    {invoice.user.addressList[0].zipcode && ` ${invoice.user.addressList[0].zipcode}`}
                                                </p>
                                                {invoice.user.addressList[0].country && (
                                                    <p>{invoice.user.addressList[0].country}</p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 mb-8">
                            <div className="flex-1 bg-white p-6 rounded-xl border border-gray-200">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 invoice-brand-soft rounded-full flex items-center justify-center">
                                        <Calendar className="w-5 h-5 invoice-brand-text" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium">Order Date</p>
                                        <p className="font-bold text-gray-900 text-lg">
                                            {reservationDateValue ? formatWallClockDate(reservationDateValue) : formatDate(order?.createdDate)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex-1 bg-white p-6 rounded-xl border border-gray-200">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center">
                                        <DollarSign className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium">Total Amount</p>
                                        <p className="font-bold text-gray-900 text-2xl">{formatCurrency(subtotal)}</p>
                                    </div>
                                </div>
                                {order?.dueAmount > 0 && order.dueAmount !== totalAmount && (
                                    <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg mt-2">
                                        Balance due: {formatCurrency(order.dueAmount)}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mb-8">
                            <div className="flex items-center gap-2 mb-4">
                                <FileText size={18} className="invoice-brand-text" />
                                <h3 className="text-lg font-semibold text-gray-900">{labels.space} Details</h3>
                            </div>
                            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                                                Item
                                            </th>
                                            <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                                                Booking Schedule
                                            </th>
                                            <th scope="col" className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 tracking-wider">
                                                Price
                                            </th>
                                            <th scope="col" className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 tracking-wider">
                                                Amount
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {lineItems.length > 0 ? (
                                            lineItems.map((item, index) => {
                                                const product = item.scheduledProduct?.product || item.product;
                                                const scheduledProduct = getScheduledProduct(item);
                                                const bookingDuration = formatBookingDuration(scheduledProduct);
                                                const imageUrl = product?.imageContents?.[0]?.displayLink || '';
                                                const price = product?.price?.priceAmt || 0;
                                                const amount = subtotal;

                                                return (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        <td className="px-4 sm:px-6 py-4">
                                                            <div className="flex items-start gap-3">
                                                                {imageUrl ? (
                                                                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                                                                        <img
                                                                            src={imageUrl}
                                                                            alt={product?.name || 'Product'}
                                                                            className="w-full h-full object-cover"
                                                                            onError={(e) => {
                                                                                e.target.style.display = 'none';
                                                                                e.target.parentElement.innerHTML = `
                                    <div class="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                                        <svg class="text-gray-400" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                            <polyline points="21 15 16 10 5 21"></polyline>
                                        </svg>
                                    </div>
                                `;
                                                                            }}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-200">
                                                                        <ImageIcon className="text-gray-500" size={18} />
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <p className="font-semibold text-gray-950 mb-1">
                                                                        {product?.name || scheduledProduct?.name || 'Unnamed Booking'}
                                                                    </p>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                                                            {product?.category?.name || 'Workspace'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 sm:px-6 py-4">
                                                            {bookingDuration ? (
                                                                <div className="space-y-2">
                                                                    <div className="flex items-center gap-2">
                                                                        <CalendarDays className="w-3.5 h-3.5 text-gray-500" />
                                                                        <span className="text-sm text-gray-900 font-medium">
                                                                            {bookingDuration.dateStr}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2 ml-0.5">
                                                                        <Clock className="w-3.5 h-3.5 text-gray-500" />
                                                                        <span className="text-sm text-gray-700">
                                                                            {bookingDuration.startTime} - {bookingDuration.endTime}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2 ml-0.5">
                                                                        <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                        </svg>
                                                                        <span className="text-sm text-gray-700">
                                                                            {bookingDuration.durationStr}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="text-gray-500 text-sm">
                                                                    <div className="flex items-center gap-2">
                                                                        <CalendarDays className="w-3.5 h-3.5 text-gray-400" />
                                                                        <span>No schedule specified</span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 sm:px-6 py-4 text-right">
                                                            <div className="space-y-1">
                                                                <div className="text-sm text-gray-700">
                                                                    {formatCurrency(price)}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    * {bookingDuration?.durationStr
                                                                        ? bookingDuration.durationStr.charAt(0).toUpperCase() + bookingDuration.durationStr.slice(1).toLowerCase()
                                                                        : 'Hours'}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 sm:px-6 py-4 text-right">
                                                            <div className="space-y-1">
                                                                <div className="font-medium text-gray-900 text-base">
                                                                    {formatCurrency(amount)}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                                                    No items in this {documentType.toLowerCase()}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="invoice-brand-soft rounded-xl p-6 border mb-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Amount Summary</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Subtotal:</span>
                                    <span className="font-medium">{formatCurrency(subtotal)}</span>
                                </div>

                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Taxes:</span>
                                    <span className="font-medium">{formatCurrency(order?.tax || 0)}</span>
                                </div>

                                {order?.shippingAndHandlingAmount > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Shipping:</span>
                                        <span className="font-medium">{formatCurrency(order.shippingAndHandlingAmount)}</span>
                                    </div>
                                )}

                                {order?.totalDiscountAmt > 0 && (
                                    <div className="flex justify-between text-sm text-red-600">
                                        <span className="text-gray-600">Discount:</span>
                                        <span className="font-medium">-{formatCurrency(order.totalDiscountAmt)}</span>
                                    </div>
                                )}

                                <div className="border-t pt-3 mt-3" style={{ borderColor: 'var(--invoice-primary-border)' }}>
                                    <div className="flex justify-between items-center">
                                        <span className="text-lg font-semibold text-gray-900">Total:</span>
                                        <span className={`text-2xl font-bold ${isPaid ? 'text-green-600' : 'invoice-brand-text'}`}>
                                            {formatCurrency(subtotal)}
                                        </span>
                                    </div>
                                    {isPaid && (
                                        <div className="flex items-center gap-2 mt-2">
                                            <span className="text-xs text-gray-500 ml-auto">
                                                On {formatDateTime(invoice.createdDate)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {(invoice.memo || invoice.notes || order?.notes) && (
                            <div className="mb-8 p-4 border rounded-lg invoice-brand-soft">
                                <h4 className="text-sm font-semibold invoice-brand-text mb-2">Notes</h4>
                                <p className="text-sm invoice-brand-text">{invoice.memo || invoice.notes || order?.notes}</p>
                            </div>
                        )}

                        <div className="pt-6 border-t border-gray-200">
                            <div className="text-center">
                                <p className="text-lg invoice-brand-text font-medium mb-3">Thank you for your business!</p>
                                <button
                                    onClick={() => navigate("/")}
                                    className="invoice-brand-home-link font-semibold hover:underline transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 rounded px-4 py-2"
                                    type="button"
                                >
                                    {invoice.company?.name || 'Back to Home'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                    .invoice-brand-gradient {
                        background: linear-gradient(to right, var(--invoice-primary), var(--invoice-secondary));
                    }

                    .invoice-brand-text {
                        color: var(--invoice-primary) !important;
                    }

                    .invoice-brand-soft {
                        background-color: var(--invoice-primary-soft);
                        border-color: var(--invoice-primary-border);
                    }

                    .invoice-brand-action:hover {
                        color: var(--invoice-primary) !important;
                        background-color: var(--invoice-primary-soft);
                        border-color: var(--invoice-primary) !important;
                    }

                    .invoice-brand-button {
                        color: var(--invoice-primary);
                        border-color: var(--invoice-primary);
                    }

                    .invoice-brand-button:hover {
                        color: #ffffff;
                        background-color: var(--invoice-primary);
                    }

                    .invoice-brand-link-button {
                        color: var(--invoice-primary);
                        border-color: var(--invoice-primary-border);
                    }

                    .invoice-brand-link-button:hover {
                        border-color: var(--invoice-primary);
                    }

                    .invoice-brand-home-link {
                        color: var(--invoice-primary);
                        background-color: var(--invoice-primary-soft);
                        --tw-ring-color: var(--invoice-primary-border);
                    }

                    .invoice-brand-home-link:hover {
                        color: var(--invoice-secondary);
                        background-color: var(--invoice-primary-border);
                    }

                    @media print {
                        body {
                            background: white !important;
                            padding: 0 !important;
                            margin: 0 !important;
                        }
                        
                        .receipt-container {
                            box-shadow: none !important;
                            border: none !important;
                            border-radius: 0 !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            max-width: 100% !important;
                        }
                        
                        .bg-gradient-to-r {
                            background: linear-gradient(to right, ${primaryColor}, ${secondaryColor}) !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        
                        .bg-gradient-to-br {
                            background: linear-gradient(to bottom right, color-mix(in srgb, ${primaryColor} 14%, transparent), color-mix(in srgb, ${secondaryColor} 14%, transparent)) !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        
                        .from-blue-50 {
                            background-color: color-mix(in srgb, ${primaryColor} 14%, transparent) !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        
                        .border-blue-100 {
                            border-color: var(--flex-primary-soft, #dbeafe) !important;
                        }
                        
                        .text-blue-600 {
                            color: ${primaryColor} !important;
                        }
                        
                        .text-green-600 {
                            color: #059669 !important;
                        }
                        
                        .text-gray-500 {
                            color: #6b7280 !important;
                        }
                        
                        .text-gray-900 {
                            color: #111827 !important;
                        }
                        
                        a {
                            color: ${primaryColor} !important;
                            text-decoration: none !important;
                        }
                        
                        button, [style*="padding-top: 120px"] {
                            display: none !important;
                        }
                    }
                `}</style>
            {showDownloadModal && invoice && (
                <InvoiceDownload
                    invoice={invoice}
                    order={order}
                    documentRef={receiptContainerRef}
                    documentType={documentType}
                    onClose={() => setShowDownloadModal(false)}
                />
            )}

        </div>
    );
}

export default ViewInvoice;
